<!-- src/components/Common/GlobalLoadingSpinner.vue -->
<template>
  <div
    v-if="isLoading"
    class="fixed inset-0 z-40 flex flex-col items-center justify-center bg-[#FFFDF5] bg-opacity-90"
    role="alert"
    aria-live="polite"
  >
    <div class="loader"></div>

    <p class="mt-4 animate-pulse text-lg text-gray-700">
      {{ statusText }}
    </p>

    <!-- Subtle hint if things are taking a while -->
    <p v-if="showSoftWarning" class="mt-2 text-xs text-gray-500">
      Still working&hellip; large queries or a cold function can take a bit.
    </p>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { eventBus } from '@/utils/eventBus'

export default {
  name: 'GlobalLoadingSpinner',
  /**
   * You can tune these without code changes by passing props where you mount the component.
   * <GlobalLoadingSpinner :softWarningMs="12000" :hardTimeoutMs="60000" />
   */
  props: {
    softWarningMs: { type: Number, default: 12000 }, // show "still working..." message
    hardTimeoutMs: { type: Number, default: 60000 }, // emit timeout + auto-stop
  },
  setup(props) {
    const isLoading = ref(false)
    const showSoftWarning = ref(false)
    const statusText = computed(() => (showSoftWarning.value ? 'Loading…' : 'Loading...'))

    let softWarningTimer = null
    let hardTimeoutTimer = null

    const clearTimers = () => {
      if (softWarningTimer) {
        clearTimeout(softWarningTimer)
        softWarningTimer = null
      }
      if (hardTimeoutTimer) {
        clearTimeout(hardTimeoutTimer)
        hardTimeoutTimer = null
      }
    }

    const lockScroll = () => {
      document.body.style.overflow = 'hidden'
    }
    const unlockScroll = () => {
      document.body.style.overflow = ''
    }

    const startLoading = () => {
      // If a load is already in progress, reset timers and continue.
      clearTimers()
      isLoading.value = true
      showSoftWarning.value = false
      lockScroll()

      // Only *hint* after softWarningMs — do not stop automatically.
      softWarningTimer = setTimeout(() => {
        if (isLoading.value) showSoftWarning.value = true
      }, props.softWarningMs)

      // After hardTimeoutMs, stop the overlay and emit a signal so callers can react.
      hardTimeoutTimer = setTimeout(() => {
        if (!isLoading.value) return
        console.warn(
          '⏰ GlobalLoadingSpinner hard-timeout reached; stopping overlay and emitting loading-timeout'
        )
        stopLoading()
        // Let listeners (e.g., a route or the customer page) decide what to do:
        eventBus.emit('loading-timeout')
      }, props.hardTimeoutMs)
    }

    const stopLoading = () => {
      clearTimers()
      isLoading.value = false
      showSoftWarning.value = false
      unlockScroll()
    }

    onMounted(() => {
      eventBus.on('loading-start', startLoading)
      eventBus.on('loading-stop', stopLoading)
    })

    onBeforeUnmount(() => {
      clearTimers()
      eventBus.off?.('loading-start', startLoading)
      eventBus.off?.('loading-stop', stopLoading)
      unlockScroll()
    })

    return {
      isLoading,
      statusText,
      showSoftWarning,
    }
  },
}
</script>

<style scoped>
/* Simple spinner reused from your existing styles */
.loader {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(79, 70, 229, 0.25);
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
